import React, { useMemo, useState } from 'react';
import { Alert, Button, Form, Modal } from 'react-bootstrap';
import { apiPost } from '../../services/api';

function formatEur(value) {
  const amount = Number(value);
  if (Number.isNaN(amount)) return '0.00';
  return amount.toFixed(2);
}

export default function AdminInvoiceRefundModal({
  invoiceId,
  invoiceTotal,
  refundedAmount,
  token,
  onRefunded,
  onClose,
}) {
  const refundable = useMemo(() => {
    const total = Number(invoiceTotal) || 0;
    const refunded = Number(refundedAmount) || 0;
    return Math.max(0, Math.round((total - refunded) * 100) / 100);
  }, [invoiceTotal, refundedAmount]);

  const [amount, setAmount] = useState(() => String(refundable));
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const parsedAmount = Number(amount);
  const amountInvalid = !Number.isFinite(parsedAmount) || parsedAmount < 0.01 || parsedAmount > refundable;
  const reasonInvalid = reason.trim().length < 10;
  const canSubmit = !submitting && !amountInvalid && !reasonInvalid && refundable > 0;

  async function handleSubmit(e) {
    e.preventDefault();
    if (!canSubmit || !token) return;

    setSubmitting(true);
    setError('');
    setSuccess(false);

    try {
      const res = await apiPost(
        `/api/v1/admin/invoices/${invoiceId}/refund`,
        {
          amount: parsedAmount,
          reason: reason.trim(),
        },
        token
      );

      if (res.status === 502) {
        setError('Stripe refund failed. Please try again or check Stripe dashboard.');
        return;
      }

      if (res.status === 422) {
        setError('Please check the refund amount and reason.');
        return;
      }

      if (!res.ok) {
        setError('Could not issue refund. Please try again.');
        return;
      }

      setSuccess(true);
      onRefunded?.();
    } catch {
      setError('Could not issue refund. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal show onHide={onClose} centered data-testid="rwm-admin-refund-modal">
      <Modal.Header closeButton>
        <Modal.Title>Issue refund</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <p className="mb-3" data-testid="rwm-refund-available">
            Refundable: €{formatEur(refundable)}
          </p>

          {success ? (
            <Alert variant="success" className="mb-0">
              Refund issued successfully
            </Alert>
          ) : (
            <>
              {error ? (
                <Alert variant="danger" className="py-2">
                  {error}
                </Alert>
              ) : null}

              <Form.Group className="mb-3" controlId="rwmRefundAmount">
                <Form.Label>Amount (EUR)</Form.Label>
                <Form.Control
                  type="number"
                  min="0.01"
                  step="0.01"
                  max={refundable}
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  disabled={submitting}
                  data-testid="rwm-refund-amount"
                />
              </Form.Group>

              <Form.Group className="mb-0" controlId="rwmRefundReason">
                <Form.Label>Reason</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  maxLength={255}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  disabled={submitting}
                  data-testid="rwm-refund-reason"
                />
                <Form.Text muted>Minimum 10 characters.</Form.Text>
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={onClose} disabled={submitting}>
            {success ? 'Close' : 'Cancel'}
          </Button>
          {!success ? (
            <Button
              type="submit"
              variant="danger"
              disabled={!canSubmit}
              data-testid="rwm-refund-submit"
            >
              {submitting ? 'Processing…' : 'Issue Refund'}
            </Button>
          ) : null}
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
